import { createRouter, createWebHistory } from '@ionic/vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { trackingEventViewPage } from '~/composables/tracking'
import { getTrackingAttributionFromQuery } from '~/utils/trackingAttribution'
import middlewareAuth from '~/router/middleware/auth'
import middlewareMobileRoot from '~/router/middleware/mobileRoot'
import middlewareOnboard from '~/router/middleware/onboard'
import type { ViewSource } from '~/types/route'
import * as Sentry from '@sentry/capacitor'
import { useAppStatusStore, useTrackStore } from '~/store'

/**
 * @description Mobile app routes.
 */
const routes: Readonly<RouteRecordRaw[]> = [
  {
    path: '/',
    redirect() {
      return middlewareMobileRoot()
    }
  },
  {
    name: 'home',
    path: '/home',
    component: () => import('~/views/home/ViewHome.vue'),
    meta: {
      requiresRestaurant: true
    }
  },
  {
    name: 'config',
    path: '/config',
    component: () => import('~/views/order/ViewOrderConfig.vue'),
    meta: {
      onboardingFlow: true
    }
  },
  {
    name: 'order-at-table',
    path: '/order-at-table/:restaurantId',
    component: () => import('~/views/order/ViewOrderAtTable.vue'),
    meta: {
      requiresRestaurant: true
    }
  },
  {
    name: 'restaurants',
    path: '/restaurants',
    component: () => import('~/views/restaurants/ViewRestaurants.vue')
  },
  {
    name: 'restaurant-detail',
    path: '/restaurants/:id',
    component: () => import('~/views/restaurants/ViewRestaurantItem.vue')
  },
  {
    name: 'menu-search',
    path: '/menu/search',
    component: () => import('~/views/menu/ViewMenuSearch.vue')
  },
  {
    name: 'menu',
    path: '/menu',
    component: () => import('~/views/menu/ViewMenu.vue')
  },
  {
    name: 'menu-product',
    path: '/menu/product/:id',
    component: () => import('~/views/menu/product/ViewProductItem.vue'),
    props: (route) => ({
      id: route.params.id,
      title: route.query?.title
    })
  },
  {
    name: 'menu-product-customisation',
    path: '/menu/product/:id/customise',
    component: () => import('~/views/menu/product/ViewProductItemCustomise.vue')
  },
  {
    name: 'menu-product-nutrition',
    path: '/menu/product/:id/nutritionalinfo',
    component: () => import('~/views/menu/product/ViewProductItemNutrition.vue')
  },
  {
    name: 'menu-product-list',
    path: '/menu/product/:id/productchoices',
    component: () => import('~/views/menu/product/ViewProductList.vue'),
    props: (route) => ({
      parentStoreId: route.query?.parentStoreId
    })
  },
  {
    name: 'menu-product-custom',
    path: '/menu/customproduct/:id',
    component: () => import('~/views/menu/product/ViewCustomProductItem.vue'),
    props: (route) => ({
      id: route.params.id,
      title: route.query?.title,
      baseIsFree:
        route.query?.baseIsFree instanceof Array
          ? route.query.baseIsFree[0]?.toLowerCase() === 'true'
          : route.query.baseIsFree?.toLowerCase() === 'true',
      storeId: route.query?.storeId,
      parentStoreId: route.query?.parentStoreId
    })
  },
  {
    name: 'menu-product-custom-customisation',
    path: '/menu/customproduct/:id/customise',
    component: () => import('~/views/menu/product/ViewCustomProductItemCustomise.vue'),
    props: (route) => ({
      storeId: route.query?.storeId
    })
  },
  {
    name: 'menu-product-custom-nutrition',
    path: '/menu/customproduct/:id/nutritionalinfo',
    component: () => import('~/views/menu/product/ViewCustomProductItemNutrition.vue'),
    props: (route) => ({
      storeId: route.query?.storeId
    })
  },
  {
    name: 'checkout',
    path: '/checkout',
    component: () => import('~/views/checkout/ViewCheckout.vue'),
    props: (route) => ({ replaceBackPath: route.query.replaceBackPath })
  },
  {
    name: 'relish',
    path: '/relish',
    component: () => import('~/views/relish/ViewRelish.vue'),
    meta: {
      requiresAuth: true
    }
  },
  {
    name: 'relish-coupon-item',
    path: '/relish/coupons/:id',
    component: () => import('~/views/relish/coupons/ViewCouponItem.vue'),
    meta: {
      requiresAuth: true
    }
  },
  {
    name: 'profile',
    path: '/profile',
    component: () => import('~/views/profile/ViewSettings.vue')
  },
  {
    name: 'mad-bunday',
    path: '/profile/mad-bunday',
    component: () => import('~/views/profile/mad-bunday/ViewMadBunday.vue'),
    props: (route) => ({ source: route.query?.source, promo: route.query?.promo }),
    meta: {
      requiresAuth: true
    }
  },
  {
    name: 'edit-profile',
    path: '/profile/edit',
    component: () => import('~/views/profile/edit/ViewEditProfile.vue'),
    meta: {
      requiresAuth: true
    }
  },
  {
    name: 'student-sign-up',
    path: '/profile/edit/student-sign-up',
    component: () => import('~/views/profile/edit/ViewStudentSignUp.vue'),
    meta: {
      requiresAuth: true
    }
  },
  {
    name: 'student-verification',
    path: '/profile/student-verification',
    component: () => import('~/views/profile/edit/StudentVerification/ViewStudentVerification.vue'),
    meta: {
      requiresAuth: true
    }
  },
  {
    name: 'edit-password',
    path: '/profile/edit/password',
    component: () => import('~/views/profile/edit/ViewEditPassword.vue'),
    meta: {
      requiresAuth: true
    }
  },
  {
    name: 'comms-preferences',
    path: '/profile/comms/comms-preferences',
    component: () => import('~/views/profile/comms/ViewCommsPreferences.vue'),
    meta: {
      requiresAuth: true
    }
  },
  {
    name: 'burger-pass',
    path: '/profile/burger-pass',
    component: () => import('~/views/profile/subscriptions/ViewBurgerPass.vue'),
    meta: {
      requiresAuth: false
    }
  },
  {
    name: 'device-permissions',
    path: '/profile/device-permissions',
    component: () => import('~/views/profile/ViewDevicePreferences.vue'),
    meta: {
      requiresAuth: false
    }
  },
  {
    name: 'referrals',
    path: '/profile/referrals',
    component: () => import('~/views/profile/referrals/ViewReferrals.vue'),
    meta: {
      requiresAuth: true
    }
  },
  {
    name: 'onboard',
    path: '/onboard',
    component: () => import('~/views/onboard/ViewOnboard.vue'),
    meta: {
      onboardingFlow: true
    }
  },
  {
    name: 'permissions-location',
    path: '/permissions/location',
    component: () => import('~/views/permissions/ViewPermissionsLocation.vue'),
    meta: {
      onboardingFlow: true
    }
  },
  {
    name: 'permissions-notifications',
    path: '/permissions/notifications',
    meta: {
      onboardingFlow: true
    },
    component: () => import('~/views/permissions/ViewPermissionsNotifications.vue')
  },
  {
    name: 'history',
    path: '/history',
    component: () => import('~/views/order/ViewOrderHistory.vue'),
    meta: {
      requiresAuth: true
    }
  },
  {
    name: 'history-item',
    path: '/history/:id',
    component: () => import('~/views/order/ViewOrderHistoryItem.vue')
  },
  {
    name: 'app-reset',
    path: '/app-reset',
    meta: {
      onboardingFlow: true,
      requiresNoUpdateCheck: true
    },
    component: () => import('~/views/AppResetView.vue')
  },
  {
    path: '/update-required',
    name: 'UpdateRequired',
    component: () => import('~/views/UpdateRequiredView.vue'),
    meta: {
      requiresNoUpdateCheck: true,
      onboardingFlow: true // do not redirect to onboarding
    }
  },
  {
    name: 'external-page',
    path: '/external',
    props: (route) => ({
      pageUrl: route.query?.pageUrl,
      pageTitle: route.query?.title
    }),
    component: () => import('~/views/ViewExternalPage.vue')
  },
  {
    name: 'not-found',
    path: '/:pathMatch(.*)*',
    redirect: '/home',
    meta: { requiresNoUpdateCheck: true } // A meta field to prevent the guard from running on this page
  }
]

const mobileRouter = createRouter({
  history: createWebHistory(import.meta.env.VITE_BASE_URL),
  routes
})

mobileRouter.beforeEach(async (to, from) => {
  Sentry.addBreadcrumb({
    category: 'router',
    message: `Navigating to ${to.fullPath}`,
    level: 'debug',
    type: 'navigation',
    data: { to: to.fullPath, params: to.params, query: to.query, from: from.fullPath }
  })

  const attribution = getTrackingAttributionFromQuery(to.query as Record<string, string | string[] | null | undefined>)
  if (attribution) {
    useTrackStore().setAttribution(attribution)
  } else {
    useTrackStore().getActiveAttribution()
  }

  // Route Guard: if app requires update, redirect to /update-required
  if (!to.meta.requiresNoUpdateCheck) {
    const appStatusStore = useAppStatusStore()

    // First, check if the app is already blocking
    if (appStatusStore.updateRequired) {
      // If we're already blocking, just redirect to the update page
      if (to.name !== 'UpdateRequired') {
        console.warn('App update is required, redirecting to UpdateRequired page')

        return '/update-required'
      }
      return // Stay on the update page
    }

    // Do not await the api request here as the delay may result in unreliable navigation
    // we will rely on checking at app launch or resume, and on previous navigation
    appStatusStore.checkAppVersion()
  }

  // Route Guard: if not authenticated, redirect to /home.
  if (to.meta.requiresAuth) {
    const redirect = middlewareAuth()

    if (redirect) {
      Sentry.addBreadcrumb({
        category: 'router',
        message: `Redirecting from ${from.fullPath} for authentication`,
        level: 'debug',
        type: 'navigation',
        data: { to: redirect, from: from.fullPath }
      })
      return redirect
    }
  }

  // Route Guard: if not onboarded and not in onboarding flow, redirect to /onboard.
  // Route Guard: if no restaurant set, redirect to /config.
  if (!to.meta.onboardingFlow || to.meta.requiresRestaurant) {
    const redirect = await middlewareOnboard(
      {
        source: to.query.source as ViewSource | undefined,
        restaurantId: to.params.restaurantId as string | undefined
      },
      to.query
    )

    if (redirect) {
      Sentry.addBreadcrumb({
        category: 'router',
        message: `Redirecting from ${from.fullPath} for onboarding or restaurant setup`,
        level: 'debug',
        type: 'navigation',
        data: { to: redirect, from: from.fullPath }
      })
      return redirect
    }
  }
})

mobileRouter.afterEach((to, from) => {
  Sentry.addBreadcrumb({
    category: 'router',
    message: `Navigation complete to ${to.fullPath}`,
    level: 'debug',
    type: 'navigation',
    data: { to: to.fullPath, params: to.params, query: to.query, from: from.fullPath }
  })
  // Track page view events, unless it's the app reset page.
  if (to.path !== '/app-reset') {
    trackingEventViewPage({ content_name: to.path })
  }
})

export default mobileRouter
